import { Noise3 } from '../math/noise';
import { hashSeed, mulberry32 } from '../math/rng';
import type { CaveSdf, FieldSample, V3 } from './sdf';
import type { ChunkMesh, ChunkSpec } from './surfaceNets';

/**
 * What grows on a cave's walls (no three.js: this runs in the cave worker).
 *
 * Stalagmites and boulders are rock: they're merged into the chunk's mesh, so they share
 * its material and its collider and cost no extra draw calls. Crystals and glowing fungi
 * are listed (`glow`, GLOW_STRIDE numbers each) for the main thread to draw as instances
 * with their light; their solid hulls go in `hullPositions`/`hullIndices`.
 *
 * Everything grows along the wall's normal, "up" toward the middle of its space, which is
 * where the cave's own gravity says up is.
 */

/** A glowing prop: kind, position (cave-local), up, scale, glow colour. */
export const GLOW_STRIDE = 11;
export const CRYSTAL = 0;
export const FUNGUS = 1;

/** Nothing grows this close to a gate (m): the joint with the entrance stays clean. */
const GATE_CLEAR = 9;
/** Crystals and fungi at least this big (scale) are solid. */
const SOLID_CRYSTAL = 0.3;
const SOLID_FUNGUS = 0.2;
/** No recipe asks for more than this chance per wall vertex (a cheap first test). */
const MAX_CHANCE = 0.05;

const _s: FieldSample = { phi: 0, s: 0, a: 1, rho: 1, owner: -1 };

/** Stalagmites grow in groves: a slow noise, per cave. */
const groves = new Map<number, Noise3>();
function grove(seed: number, x: number, y: number, z: number): number {
  let n = groves.get(seed);
  if (!n) {
    n = new Noise3(hashSeed(seed, 0x67));
    if (groves.size > 8) groves.clear();
    groves.set(seed, n);
  }
  const v = n.noise(x * 0.09, y * 0.09, z * 0.09);
  return Math.min(Math.max((v + 0.15) / 0.6, 0), 1) * 2.2;
}

/** Growing triangle lists. */
class Lists {
  readonly positions: number[] = [];
  readonly normals: number[] = [];
  readonly looks: number[] = [];
  readonly indices: number[] = [];
  get count(): number {
    return this.positions.length / 3;
  }
  vertex(p: V3, n: V3, look: ArrayLike<number>): void {
    this.positions.push(p[0], p[1], p[2]);
    this.normals.push(n[0], n[1], n[2]);
    this.looks.push(look[0], look[1], look[2], look[3]);
  }
}

/**
 * Decorate a meshed chunk from its voids' recipes (PropRecipe). Each wall vertex rolls its
 * own dice, hashed from its position, so the props don't depend on the chunking; only the
 * chunk whose box holds a vertex decorates it.
 */
export function decorateChunk(m: ChunkMesh, sdf: CaveSdf, spec: ChunkSpec, seed: number): ChunkMesh {
  const rock = new Lists();
  const hull = new Lists();
  const glow: number[] = [];
  const gates = sdf.shape.gates ?? [];
  const size = spec.n * spec.cell;
  const [ox, oy, oz] = spec.origin;
  const cell2 = spec.cell * spec.cell;
  const look = new Float32Array(4);
  const count = m.positions.length / 3;
  for (let v = 0; v < count; v++) {
    const x = m.positions[v * 3];
    const y = m.positions[v * 3 + 1];
    const z = m.positions[v * 3 + 2];
    if (x < ox || y < oy || z < oz || x >= ox + size || y >= oy + size || z >= oz + size) continue;
    const h = hashSeed(seed, Math.floor(x * 64), Math.floor(y * 64), Math.floor(z * 64));
    const u = h / 4294967296;
    if (u >= MAX_CHANCE) continue;
    if (gates.some((g) => Math.hypot(x - g.p[0], y - g.p[1], z - g.p[2]) < GATE_CLEAR)) continue;
    const s = sdf.sample(x, y, z, _s);
    if (s.owner < 0) continue;
    const recipe = sdf.shape.voids[s.owner].props;
    if (!recipe) continue;
    const n: V3 = [m.normals[v * 3], m.normals[v * 3 + 1], m.normals[v * 3 + 2]];
    // Wall area per vertex: surface nets put one vertex in each cell the wall crosses.
    const area = cell2 / (Math.abs(n[0]) + Math.abs(n[1]) + Math.abs(n[2]));
    const chances = [
      (recipe.stalagmites ?? 0) * grove(seed, x, y, z),
      recipe.boulders,
      recipe.crystals,
      recipe.fungi,
    ];
    // Bigger spaces grow bigger props (rho: how deep the space's middle is here).
    const rho = s.rho;
    const big = Math.min(Math.max(rho / 6, 0.6), 2.5);
    let kind = -1;
    let acc = 0;
    for (let k = 0; k < 4; k++) {
      acc += (chances[k] ?? 0) * area;
      if (u < acc) {
        kind = k;
        break;
      }
    }
    if (kind < 0) continue;
    const rnd = mulberry32(h);
    const p: V3 = [x, y, z];
    for (let k = 0; k < 4; k++) look[k] = m.looks[v * 4 + k];
    if (kind === 0) stalagmite(rock, p, n, look, rnd, sdf, big);
    else if (kind === 1) boulder(rock, p, n, look, rnd);
    else {
      const crystal = kind === 2;
      // Sized to the space (TreeRenderer's shapes are ~4 m and ~6 m tall at scale 1): small
      // in passages, where you walk past them, big in rooms, and never past the far wall.
      const most = crystal ? 0.1 + 0.05 * rho : 0.06 + 0.03 * rho;
      let scale = Math.min(crystal ? 0.22 + rnd() * 0.3 : 0.12 + rnd() * 0.24, most);
      const tall = crystal ? 4.2 : 6;
      scale = fitToSpace(sdf, p, n, scale, (sc) => tall * sc + 0.6);
      if (scale < 0.08) continue;
      const g = recipe.glow ?? [0.6, 0.8, 1.2];
      glow.push(crystal ? CRYSTAL : FUNGUS, x, y, z, n[0], n[1], n[2], scale, g[0], g[1], g[2]);
      // Only the big ones are solid: small ones don't stop you in a passage.
      if (crystal && scale >= SOLID_CRYSTAL) prism(hull, p, n, 1.0 * scale, -0.2, 3.2 * scale);
      else if (!crystal && scale >= SOLID_FUNGUS) {
        prism(hull, p, n, 0.7 * scale, -0.2, 5.6 * scale);
        prism(hull, p, n, 3.4 * scale, 5.25 * scale, 5.85 * scale);
      }
    }
  }
  const merged = merge(m, rock);
  merged.glow = new Float32Array(glow);
  merged.hullPositions = new Float32Array(hull.positions);
  merged.hullIndices = new Uint32Array(hull.indices);
  return merged;
}

/** Shrink a prop until it (height(scale) along n) fits in the air in front of the wall. */
function fitToSpace(sdf: CaveSdf, p: V3, n: V3, scale: number, height: (s: number) => number): number {
  let s = scale;
  for (let i = 0; i < 3; i++) {
    const t = height(s);
    if (sdf.sample(p[0] + n[0] * t, p[1] + n[1] * t, p[2] + n[2] * t, _s).phi < -0.4) return s;
    s *= 0.55;
  }
  return 0;
}

/** Two directions across `up` with t1 × t2 = up. */
function across(up: V3): [V3, V3] {
  const helper: V3 = Math.abs(up[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const t1 = normalize(cross(helper, up));
  return [t1, cross(up, t1)];
}

/**
 * A stalagmite: a lumpy cone growing out of the wall along its normal (leaning a little),
 * 0.4–2.4 m tall times `big` (the space's size), shorter where the space in front is shallow.
 */
function stalagmite(
  out: Lists,
  p: V3,
  n: V3,
  look: ArrayLike<number>,
  rnd: () => number,
  sdf: CaveSdf,
  big: number,
): void {
  const H = fitToSpace(sdf, p, n, (0.4 + rnd() * rnd() * 2) * big, (h) => h + 0.8);
  if (H < 0.3) return;
  const R = H * (0.14 + rnd() * 0.12) + 0.08;
  const up = normalize(add(n, scale3(randomUnit(rnd), 0.15)));
  const [t1, t2] = across(up);
  const SIDES = 7;
  const RINGS: [number, number][] = [
    [0, 1.2],
    [0.25, 0.8],
    [0.55, 0.45],
    [0.82, 0.18],
    [1, 0.03],
  ];
  // Sunk a little into the wall, so no gap shows where the wall bulges.
  const sink = 0.3;
  const start = out.count;
  const twist = rnd() * Math.PI * 2;
  RINGS.forEach(([t, rf], i) => {
    for (let j = 0; j < SIDES; j++) {
      const a = twist + (j / SIDES) * Math.PI * 2 + i * 0.4;
      const r = R * rf * (1 + (rnd() - 0.5) * 0.3);
      const radial = add(scale3(t1, Math.cos(a)), scale3(t2, Math.sin(a)));
      const c = add(p, scale3(up, t * (H + sink) - sink));
      out.vertex(add(c, scale3(radial, r)), normalize(add(radial, scale3(up, R / H))), look);
    }
  });
  for (let i = 0; i + 1 < RINGS.length; i++) {
    for (let j = 0; j < SIDES; j++) {
      const a = start + i * SIDES + j;
      const b = start + i * SIDES + ((j + 1) % SIDES);
      const c = start + (i + 1) * SIDES + ((j + 1) % SIDES);
      const d = start + (i + 1) * SIDES + j;
      out.indices.push(a, b, c, a, c, d);
    }
  }
}

/** A boulder: a lumpy, flattened ball half sunk into the wall, 0.3–1.2 m across the short way. */
function boulder(out: Lists, p: V3, n: V3, look: ArrayLike<number>, rnd: () => number): void {
  const R = 0.35 + rnd() * rnd() * 0.9;
  const [t1, t2] = across(n);
  const sx = R * (1.1 + rnd() * 0.3);
  const sy = R * (0.55 + rnd() * 0.25);
  const sz = R * (0.9 + rnd() * 0.3);
  const ph = rnd() * 6.28;
  const { vertices, faces } = icosphere();
  const start = out.count;
  const local: V3[] = vertices.map(([x, y, z]) => {
    const k = 1 + 0.2 * Math.sin(x * 5.1 + y * 3.7 + ph) * Math.cos(z * 4.3 - x * 2.1 + ph);
    return [x * k * sx, y * k * sy + sy * 0.25, z * k * sz];
  });
  // Normals from the lumpy shape's faces.
  const normals: V3[] = local.map(() => [0, 0, 0]);
  for (const [a, b, c] of faces) {
    const fn = cross(sub(local[b], local[a]), sub(local[c], local[a]));
    for (const i of [a, b, c]) normals[i] = add(normals[i], fn);
  }
  // (x, y, z) → (t2, n, t1) keeps the frame right-handed, so the faces still face out.
  const toWorld = (q: V3): V3 => add(add(scale3(t2, q[0]), scale3(n, q[1])), scale3(t1, q[2]));
  local.forEach((q, i) => {
    out.vertex(add(p, toWorld(q)), normalize(toWorld(normals[i])), look);
  });
  for (const [a, b, c] of faces) out.indices.push(start + a, start + b, start + c);
}

/** A six-sided prism along `up` from `from` to `to` (m along it), for a solid hull. */
function prism(out: Lists, p: V3, up: V3, radius: number, from: number, to: number): void {
  const [t1, t2] = across(up);
  const start = out.count;
  const none: V3 = [0, 0, 0];
  for (const t of [from, to]) {
    for (let j = 0; j < 6; j++) {
      const a = (j / 6) * Math.PI * 2;
      const radial = add(scale3(t1, Math.cos(a) * radius), scale3(t2, Math.sin(a) * radius));
      out.vertex(add(add(p, scale3(up, t)), radial), none, [0, 0, 0, 0]);
    }
  }
  for (let j = 0; j < 6; j++) {
    const a = start + j;
    const b = start + ((j + 1) % 6);
    out.indices.push(a, b, b + 6, a, b + 6, a + 6);
  }
  // Caps.
  for (let j = 1; j < 5; j++) {
    out.indices.push(start, start + j + 1, start + j);
    out.indices.push(start + 6, start + 6 + j, start + 6 + j + 1);
  }
}

/** The chunk's mesh with the rock props appended. */
function merge(m: ChunkMesh, extra: Lists): ChunkMesh {
  if (extra.count === 0) return { ...m };
  const base = m.positions.length / 3;
  const positions = new Float32Array(m.positions.length + extra.positions.length);
  positions.set(m.positions);
  positions.set(extra.positions, m.positions.length);
  const normals = new Float32Array(m.normals.length + extra.normals.length);
  normals.set(m.normals);
  normals.set(extra.normals, m.normals.length);
  const looks = new Float32Array(m.looks.length + extra.looks.length);
  looks.set(m.looks);
  looks.set(extra.looks, m.looks.length);
  const indices = new Uint32Array(m.indices.length + extra.indices.length);
  indices.set(m.indices);
  for (let i = 0; i < extra.indices.length; i++) indices[m.indices.length + i] = extra.indices[i] + base;
  return { positions, normals, looks, indices };
}

let sphere: { vertices: V3[]; faces: [number, number, number][] } | null = null;
/** A unit icosphere, subdivided once (42 vertices, 80 faces wound outward). */
function icosphere(): { vertices: V3[]; faces: [number, number, number][] } {
  if (sphere) return sphere;
  const t = (1 + Math.sqrt(5)) / 2;
  const vertices: V3[] = [
    [-1, t, 0],
    [1, t, 0],
    [-1, -t, 0],
    [1, -t, 0],
    [0, -1, t],
    [0, 1, t],
    [0, -1, -t],
    [0, 1, -t],
    [t, 0, -1],
    [t, 0, 1],
    [-t, 0, -1],
    [-t, 0, 1],
  ].map((v) => normalize(v as V3));
  let faces: [number, number, number][] = [
    [0, 11, 5],
    [0, 5, 1],
    [0, 1, 7],
    [0, 7, 10],
    [0, 10, 11],
    [1, 5, 9],
    [5, 11, 4],
    [11, 10, 2],
    [10, 7, 6],
    [7, 1, 8],
    [3, 9, 4],
    [3, 4, 2],
    [3, 2, 6],
    [3, 6, 8],
    [3, 8, 9],
    [4, 9, 5],
    [2, 4, 11],
    [6, 2, 10],
    [8, 6, 7],
    [9, 8, 1],
  ];
  const mids = new Map<string, number>();
  const mid = (a: number, b: number) => {
    const key = a < b ? `${a},${b}` : `${b},${a}`;
    let i = mids.get(key);
    if (i === undefined) {
      i = vertices.length;
      vertices.push(normalize(scale3(add(vertices[a], vertices[b]), 0.5)));
      mids.set(key, i);
    }
    return i;
  };
  faces = faces.flatMap(([a, b, c]) => {
    const ab = mid(a, b);
    const bc = mid(b, c);
    const ca = mid(c, a);
    return [
      [a, ab, ca],
      [b, bc, ab],
      [c, ca, bc],
      [ab, bc, ca],
    ] as [number, number, number][];
  });
  sphere = { vertices, faces };
  return sphere;
}

function randomUnit(rnd: () => number): V3 {
  const z = rnd() * 2 - 1;
  const a = rnd() * Math.PI * 2;
  const s = Math.sqrt(1 - z * z);
  return [s * Math.cos(a), s * Math.sin(a), z];
}
function add(a: V3, b: V3): V3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}
function sub(a: V3, b: V3): V3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function scale3(a: V3, s: number): V3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}
function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function normalize(a: V3): V3 {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}
